import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Mail, Check } from 'lucide-react';
import { Lead } from '../data/mockLeads';
import { Button } from './Button';
export type ActionMode = 'call' | 'email';
interface LeadActionModalProps {
  lead: Lead | null;
  mode: ActionMode;
  open: boolean;
  onClose: () => void;
  onSubmit: (leadId: string, mode: ActionMode, payload: Record<string, string>) => void;
}
const CALL_OUTCOMES = ['Spoke with lead', 'Left voicemail', 'No answer', 'Wrong number'];
export function LeadActionModal({
  lead,
  mode,
  open,
  onClose,
  onSubmit
}: LeadActionModalProps) {
  const [outcome, setOutcome] = useState(CALL_OUTCOMES[0]);
  const [notes, setNotes] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // Reset/seed form whenever a new lead/mode is opened
  useEffect(() => {
    if (open && lead) {
      setOutcome(CALL_OUTCOMES[0]);
      setNotes('');
      setSubject(`Following up on your ${lead.loanPurpose.toLowerCase()} loan`);
      setBody(`Hi ${lead.name.split(' ')[0]},\n\nI wanted to follow up regarding your ${lead.loanPurpose.toLowerCase()} loan. Do you have a few minutes this week to connect?\n\nBest regards,\nYour Loan Officer`);
    }
  }, [open, lead, mode]);
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!lead) return null;
  const isCall = mode === 'call';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(lead.id, mode, isCall ? {
      outcome,
      notes
    } : {
      subject,
      body
    });
  };
  return createPortal(<AnimatePresence data-id="element-15">
      {open && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-16">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-17" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="lead-action-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.15
      }} className="relative w-full max-w-lg bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden" data-id="element-18">
            <div className="flex items-start justify-between p-6 border-b border-slate-200" data-id="element-19">
              <div className="flex items-center gap-3" data-id="element-20">
                <div className={`p-2.5 rounded-lg ${isCall ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`} data-id="element-21">
                  {isCall ? <Phone size={20} data-id="element-22" /> : <Mail size={20} data-id="element-23" />}
                </div>
                <div data-id="element-24">
                  <h2 id="lead-action-title" className="text-lg font-semibold text-slate-900" data-id="element-25">
                    {isCall ? 'Log Call' : 'Send Email'}
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-26">
                    {lead.name} · {isCall ? lead.phone : lead.email}
                  </p>
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors" data-id="element-27">
                <X size={20} data-id="element-28" />
              </button>
            </div>

            <form onSubmit={handleSubmit} data-id="element-29">
              <div className="p-6 space-y-4" data-id="element-30">
                {isCall ? <>
                    <div data-id="element-31">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-32">
                        Call outcome
                      </label>
                      <select value={outcome} onChange={e => setOutcome(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-33">
                        {CALL_OUTCOMES.map(o => <option key={o} value={o} data-id="element-34">
                            {o}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-35">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-36">
                        Notes
                      </label>
                      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder="What did you discuss?" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-37" />
                    </div>
                  </> : <>
                    <div data-id="element-38">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-39">
                        Subject
                      </label>
                      <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-40" />
                    </div>
                    <div data-id="element-41">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-42">
                        Message
                      </label>
                      <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-43" />
                    </div>
                  </>}
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50" data-id="element-44">
                <Button type="button" variant="outline" onClick={onClose} data-id="element-45">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" icon={isCall ? <Check size={16} data-id="element-47" /> : <Mail size={16} data-id="element-48" />} data-id="element-46">
                  {isCall ? 'Save Call' : 'Send Email'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>, document.body);
}